import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Container, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { debounce } from 'lodash';
import dayjs from 'dayjs';

import MemberResults from 'src/components/member/MemberListResults';
import { deleteMember, getMembers } from 'src/api/member';
import { CONSTANTS, STRINGS } from 'src/commons';
import Toolbar from 'src/components/member/Toolbar';
import { membersReducerAction } from 'src/redux/reducers/members';
import { commonReducerAction } from 'src/redux/reducers/common';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import RegisterPaymentModal from 'src/pages/MemberList/MemberModals/registerPayment';
import FilterModal from 'src/components/member/Modals/FilterModal';
import ConfirmDialog from 'src/components/ConfirmationBox';

const ExpiredMemberships = () => {
  const { startLoading, stopLoading } = loaderReducerAction;
  const [members, setMembers] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, gymId } = useSelector((state) => state.common);
  const { membersList } = useSelector((state) => state.members);
  const { updateMembers } = membersReducerAction;
  const { logoutUser } = commonReducerAction;
  const [openModal, setOpenModal] = useState(false);
  const [member, setMember] = useState({});
  const [searchLoader, setSearchLoader] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [initialValues, setIntialValues] = useState({});
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef();
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteMemberData, setDeleteMemberData] = useState({});
  const [deleting, setDeleting] = useState(false);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };

  const handleFilterModal = () => {
    setOpenFilterModal((prev) => !prev);
  };

  dayjs.extend(customParseFormat);
  const handleModal = (member) => {
    setMember(member);
    setOpenModal((prev) => !prev);
  };
  const handleNavigate = () => {
    navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.PAYMENT);
  };

  const getData = () => {
    dispatch(startLoading());
    getMembers(user, gymId)
      .then((res) => {
        if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
          dispatch(logoutUser());
        if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
          setMembers(
            res.data?.allMembers?.filter(
              (member) =>
                dayjs(member.membership_end_date, 'DD-MM-YYYY') <
                dayjs(new Date())
            )
          );
          dispatch(updateMembers(res?.data?.allMembers));
        }
        dispatch(stopLoading());
      })
      .catch((err) => {
        dispatch(stopLoading());
        console.log(err);
      });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChange = (key, value) => {
    setIntialValues({ ...initialValues, [key]: value });
  };

  const onEdit = (item) => {
    navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.EDIT_MEMBER + '/' + item._id);
  };

  const onDelete = (item) => {
    dispatch(startLoading());
    setDeleting(true);
    deleteMember(user, { member_id: item._id.toString() })
      .then((res) => {
        if (res?.status === 200) {
          setSnackbarState({
            open: true,
            message: 'Member deleted successfully',
            severity: 'success'
          });
        } else if (res?.status !== 200) {
          setSnackbarState({
            open: true,
            message: res?.message || 'Something went wrong',
            severity: 'error'
          });
        }
        setDeleting(false);
        setDeleteModal(false);
        getData();
      })
      .catch((err) => {
        setSnackbarState({
          open: true,
          message: err?.message || 'Something went wrong',
          severity: 'error'
        });
        dispatch(stopLoading());
        setDeleting(false);
        setDeleteModal(false);
      });
  };

  const filterItem = debounce((text) => {
    setSearchText(text);
  }, 200);

  return (
    <>
      <Helmet>
        <title>MemberShip | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <FilterModal
            members={membersList?.filter(
              (member) =>
                dayjs(member.membership_end_date, 'DD-MM-YYYY') <
                dayjs(new Date())
            )}
            setMembers={(membersData) => {
              setMembers(membersData);
            }}
            open={openFilterModal}
            setOpen={handleFilterModal}
            member={member}
            onReset={() => {
              setIntialValues({});
              setMembers(
                membersList?.filter(
                  (member) =>
                    dayjs(member.membership_end_date, 'DD-MM-YYYY') <
                    dayjs(new Date())
                )
              );
              if (searchInputRef?.current?.querySelector('input')?.value) {
                searchInputRef.current.querySelector('input').value = '';
              }
            }}
            handleValueChange={handleValueChange}
            initialValues={initialValues}
            searchText={searchText}
          />

          <RegisterPaymentModal
            open={openModal}
            setOpen={handleModal}
            member={member}
          />
          <Toolbar
            searchLoader={searchLoader}
            filterItem={filterItem}
            text={STRINGS.ADD_MEMBER}
            searchInputRef={searchInputRef}
            handleCloseModal={handleFilterModal}
            route={CONSTANTS.ROUTES.PRIVATE_ROUTES.ADD_MEMBER}
          />
          <Box sx={{ pt: 3 }}>
            <MemberResults
              handleOpenModal={handleModal}
              members={members}
              onEdit={onEdit}
              onDelete={(item) => {
                setDeleteMemberData(item);
                setDeleteModal(true);
              }}
              handleNavigate={handleNavigate}
            />
          </Box>
        </Container>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarState.open}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <div>
          <Alert onClose={handleClose} severity={snackbarState.severity}>
            {snackbarState.message}
          </Alert>
        </div>
      </Snackbar>
      <ConfirmDialog
        heading={'Delete Member'}
        message={'Do you want to delete the member?'}
        open={deleteModal}
        handleClose={() => {
          setDeleteModal(false);
        }}
        handleConfirm={() => {
          onDelete(deleteMemberData);
        }}
        loading={deleting}
        key={'detete modal'}
      />
    </>
  );
};

export default ExpiredMemberships;
